<script lang="ts" setup>
import { Head, Link, router, useForm, usePage } from "@inertiajs/vue3";
import HeadingComponent from "../../Components/HeadingComponent.vue";
import EventPublicWrapper from "../EventPublic/EventPublicWrapper.vue";
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from "vue";

import { Event } from "@/types";
import DialogModal from "@/Components/DialogModal.vue";
import JetLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import JetTextarea from "@/Components/Textarea.vue";
import JetButton from "@/Components/PrimaryButton.vue";
import JetCheckbox from "@/Components/Checkbox.vue";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import eventBus from "@/Helpers/eventBus";
import Button from "@/Components/Button.vue";
import { DateTime } from "luxon";

import { ChevronDownIcon, PrinterIcon } from "@heroicons/vue/24/solid/index";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import FilterButton from "./Components/FilterButton.vue";
import { competitorMatchesSearch, copyToClipboard, syncOpenWithSearch } from "@/Helpers/helperFunctions";
import CompetitorSearchInput from "@/Components/Event/CompetitorSearchInput.vue";

interface RegistrationInvite {
    id: number;
    url: string;
    created_at: string;
    used_at: string | null;
    used_by: string | null;
}

interface Props {
    event: Event;
    canRegisterForEvent: boolean;
    registration_invites: RegistrationInvite[];
}

const props = withDefaults(defineProps<Props>(), {});

const modalCompetitorNumbers = reactive({
    show: false,
    overwriteAll: false,
});
const modalSendEmail = reactive({
    show: false,
});

const modalSendEmailForm = useForm({
    subject: `Message from the Organizer (${props.event.name})`,
    contents: null,
    recipient_mode: "filters",
    recipient_statuses: [] as string[],
    recipient_class_ids: [] as number[],
    recipient_countries: [] as string[],
    recipient_competitor_ids: [] as number[],
});

const competitorStatuses = ["tester", "pending", "accepted", "rejected"];

const riderFilter = ref("");
const competitorSearch = ref("");

function riderNames(competitor): string {
    return [competitor.driver_full_name, competitor.co_driver_full_name].filter(Boolean).join(" / ");
}

function competitorCountryCodes(competitor): string[] {
    return [...new Set([competitor.driver_country, competitor.co_driver_country].filter(Boolean))];
}

const allCompetitors = computed(() =>
    props.event.classes.flatMap((eventClass) =>
        eventClass.competitors.map((competitor) => ({ ...competitor, event_class_id: eventClass.id })),
    ),
);

const competitorCountries = computed(() => {
    const codes = new Set(allCompetitors.value.flatMap(competitorCountryCodes));

    return [...codes]
        .map((code) => ({
            code,
            name: usePage().props.app.countries[code.toLowerCase()]?.name ?? code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

/**
 * The three facets narrow each other, so a facet's own counts must ignore its own selection —
 * that is what makes the class counts drop to "how many accepted" once a status is ticked.
 */
function recipientsIgnoring(facet: string | null) {
    const { recipient_statuses, recipient_class_ids, recipient_countries } = modalSendEmailForm;

    return allCompetitors.value.filter(
        (competitor) =>
            (facet === "status" ||
                !recipient_statuses.length ||
                recipient_statuses.includes(competitor.status)) &&
            (facet === "class" ||
                !recipient_class_ids.length ||
                recipient_class_ids.includes(competitor.event_class_id)) &&
            (facet === "country" ||
                !recipient_countries.length ||
                competitorCountryCodes(competitor).some((code) => recipient_countries.includes(code))),
    );
}

const selectedRecipients = computed(() => recipientsIgnoring(null));

function statusCount(status: string): number {
    return recipientsIgnoring("status").filter((competitor) => competitor.status === status).length;
}

function classCount(eventClassId: number): number {
    return recipientsIgnoring("class").filter((competitor) => competitor.event_class_id === eventClassId)
        .length;
}

function countryCount(code: string): number {
    return recipientsIgnoring("country").filter((competitor) =>
        competitorCountryCodes(competitor).includes(code),
    ).length;
}

function clearRecipientFilters() {
    modalSendEmailForm.recipient_statuses = [];
    modalSendEmailForm.recipient_class_ids = [];
    modalSendEmailForm.recipient_countries = [];
}

const filteredClasses = computed(() => {
    const needle = riderFilter.value.trim().toLowerCase();

    return props.event.classes
        .map((eventClass) => ({
            ...eventClass,
            competitors: needle
                ? eventClass.competitors.filter((c) => riderNames(c).toLowerCase().includes(needle))
                : eventClass.competitors,
        }))
        .filter((eventClass) => eventClass.competitors.length > 0);
});

function modalSendEmailFormSendEmail() {
    modalSendEmailForm.post(
        route(`modalSendEmailFormSendEmail`, {
            event: props.event,
        }),
        {
            preserveScroll: true,
            onSuccess: (response) => {
                // console.log(response);
                closeModals();
            },
        },
    );
}

function regenerateCompetitorNumbers(ordering, useClasses = false) {
    router.post(
        route("regenerateCompetitorNumbers", { event: props.event.id }),
        {
            ordering,
            useClasses,
            overwriteAll: modalCompetitorNumbers.overwriteAll,
        },
        {
            onFinish: (visit) => {
                closeModals();
            },
        },
    );
}

const modalRegistrationInvites = reactive({
    show: false,
    isCreating: false,
});

function createRegistrationInvite() {
    if (modalRegistrationInvites.isCreating) {
        return;
    }

    router.post(
        route("createRegistrationInvite", { event: props.event.id }),
        {},
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => (modalRegistrationInvites.isCreating = true),
            onFinish: () => (modalRegistrationInvites.isCreating = false),
        },
    );
}

function deleteRegistrationInvite(invite: RegistrationInvite) {
    router.delete(route("deleteRegistrationInvite", { eventRegistrationInvite: invite.id }), {
        preserveScroll: true,
        preserveState: true,
        onBefore: () => confirm("Revoke this invite link? Nobody will be able to register with it."),
    });
}

function closeModals() {
    //console.log(arguments);
    modalCompetitorNumbers.show = false;
    modalCompetitorNumbers.overwriteAll = false;
    modalSendEmail.show = false;
    modalRegistrationInvites.show = false;
}

const isCompetitorFormModalLoading = ref(false);

function editCompetitor(competitor) {
    // console.log("competitor", competitor);
    isCompetitorFormModalLoading.value = true;
    eventBus.$emit("showCompetitorFormModal", {
        type: "update",
        competitor: competitor,
    });
}

// All open for SSR / desktop; collapsed on mobile after mount.
const openClassIds = ref(new Set<number>(props.event.classes.map((eventClass) => eventClass.id)));

syncOpenWithSearch(openClassIds, competitorSearch, () =>
    props.event.classes
        .filter((eventClass) =>
            eventClass.competitors.some((competitor) =>
                competitorMatchesSearch(competitor, competitorSearch.value),
            ),
        )
        .map((eventClass) => eventClass.id),
);

function toggleClass(eventClassId: number) {
    if (openClassIds.value.has(eventClassId)) {
        openClassIds.value.delete(eventClassId);
    } else {
        openClassIds.value.add(eventClassId);
    }
}

async function scrollToElement(eventClassId: number, elementId: string) {
    openClassIds.value.add(eventClassId);
    await nextTick();
    // Images above the list (the mobile header logo has no fixed height) shift the target while they load.
    if (document.readyState !== "complete") {
        await new Promise((resolve) => window.addEventListener("load", resolve, { once: true }));
    }
    document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

let removeStartEventListener;
onMounted(() => {
    eventBus.$on("isCompetitorFormModalLoading", (val: boolean) => (isCompetitorFormModalLoading.value = val));

    if (!window.matchMedia("(min-width: 768px)").matches) {
        openClassIds.value.clear();
    }

    removeStartEventListener = router.on("navigate", (event) => {
        const urlParams = new URLSearchParams(document.location.search);
        const competitorId = Number(urlParams.get("scroll_to_cid"));
        const eventClass = props.event.classes.find((eventClass) =>
            eventClass.competitors.some((competitor) => competitor.id === competitorId),
        );
        if (eventClass) {
            scrollToElement(eventClass.id, `competitor_id_${competitorId}`);
        }
    });
});

onUnmounted(() => {
    eventBus.$off("isCompetitorFormModalLoading");

    // Remove the listener...
    removeStartEventListener();
});
</script>

<template>
    <EventPublicWrapper>
        <Head>
            <title>Event competitors: {{ $page.props.event.name }}</title>
            <meta
                property="og:title"
                head-key="og:title"
                :content="`Event competitors: ${$page.props.meta_event_name}`"
            />
        </Head>
        <div class="w-full max-w-7xl space-y-4 md:w-auto">
            <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                <template #name>Competitors ({{ $page.props.event.competitors_count }})</template>
                <template #description></template>
            </HeadingComponent>
            <HeadingComponent
                v-if="$page.props.can.view_sensitive_event"
                class="rounded-lg bg-orange-50 p-4 shadow-lg sm:px-6"
            >
                <template #name>Event administrator tools</template>
                <template #description>
                    <div class="space-y-2 pt-4 sm:space-x-4">
                        <Button @click="modalSendEmail.show = true"> Send an email to competitors</Button>

                        <Button @click="modalCompetitorNumbers.show = true">
                            Open competitor numbers generator
                        </Button>

                        <Button
                            v-if="$page.props.can.update_event"
                            @click="modalRegistrationInvites.show = true"
                        >
                            Registration invite links
                        </Button>

                        <a
                            class="inline-flex text-right"
                            href="?excel=1"
                            >Competitors as Excel</a
                        >

                        <a
                            class="inline-flex"
                            :href="
                                route('event.competitor-entry-form-pdf', {
                                    event_hashid: $page.props.event.hashid,
                                })
                            "
                            >Entry forms in one PDF file</a
                        >
                    </div>
                </template>
            </HeadingComponent>
            <div
                v-if="event.classes.length > 1"
                class="sticky top-[4.25rem] z-10 flex gap-2 overflow-x-auto rounded-lg bg-white p-2 shadow-lg md:top-0"
            >
                <FilterButton
                    v-for="eventClass in event.classes"
                    :key="'cbtn' + eventClass.id"
                    class="shrink-0 whitespace-nowrap"
                    @click="scrollToElement(eventClass.id, `class_id_${eventClass.id}`)"
                >
                    {{ eventClass.name }} ({{ eventClass.competitors.length }})
                </FilterButton>
            </div>
            <div class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                <CompetitorSearchInput v-model="competitorSearch" />
            </div>
            <div class="overflow-auto rounded border border-gray-200 bg-white shadow">
                <table class="w-full table-auto">
                    <thead>
                        <tr>
                            <!--                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Team name</th>-->
                            <th
                                scope="col"
                                class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                            >
                                No.
                            </th>
                            <th
                                scope="col"
                                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            ></th>
                            <th
                                scope="col"
                                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                                Competitor
                            </th>
                            <th
                                scope="col"
                                class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
                            >
                                Vehicle <br />(class)
                            </th>
                            <th
                                scope="col"
                                class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
                            >
                                Status
                            </th>
                            <th
                                v-if="$page.props.can.view_sensitive_event"
                                scope="col"
                                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                                Entry fee paid
                            </th>
                            <th
                                v-if="$page.props.can.view_sensitive_event"
                                scope="col"
                                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                                Registered at
                            </th>
                            <th
                                v-if="$page.props.can.view_sensitive_event"
                                scope="col"
                                class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                                Comment
                            </th>
                            <th v-if="$page.props.can.view_sensitive_event"></th>
                        </tr>
                    </thead>
                    <tbody class="bg-white">
                        <template
                            v-for="eventClass in event.classes"
                            :key="'cid' + eventClass.id"
                        >
                            <tr class="border-t border-gray-200">
                                <th
                                    :id="`class_id_${eventClass.id}`"
                                    colspan="11"
                                    scope="colgroup"
                                    class="scroll-mt-[8.5rem] bg-gray-50 p-0 text-left text-sm font-semibold text-gray-900 md:scroll-mt-16"
                                >
                                    <button
                                        type="button"
                                        class="flex w-full items-center justify-between px-4 py-2 text-left sm:px-6"
                                        :aria-expanded="openClassIds.has(eventClass.id)"
                                        @click="toggleClass(eventClass.id)"
                                    >
                                        {{ eventClass.name }} ({{ eventClass.competitors.length }})
                                        <ChevronDownIcon
                                            :class="[
                                                openClassIds.has(eventClass.id) ? 'rotate-180' : '',
                                                'size-5 text-gray-400 transition-transform',
                                            ]"
                                        />
                                    </button>
                                </th>
                            </tr>
                            <tr
                                v-for="(competitor, competitorIndex) in eventClass.competitors"
                                v-show="
                                    openClassIds.has(eventClass.id) &&
                                    competitorMatchesSearch(competitor, competitorSearch)
                                "
                                :key="'compid' + competitor.id"
                                :class="[
                                    competitorIndex === 0 ? 'border-gray-300' : 'border-gray-200',
                                    'border-t',
                                    $page.props.user?.id === competitor.user_id ? 'font-bold' : '',
                                ]"
                            >
                                <!--                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ competitor.team_name }}</td>-->
                                <td
                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-orange-400 sm:pl-6"
                                >
                                    {{ competitor.competitor_number ?? "-" }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-4 text-sm">
                                    <a
                                        v-if="$page.props.user?.can_impersonate && competitor.user_id"
                                        :href="route('users.impersonate', { userId: competitor.user_id })"
                                        class="text-xs text-indigo-400 hover:underline"
                                        @click.stop
                                    >
                                        <span
                                            v-if="competitor.driver_country"
                                            :title="
                                                $page.props.app.countries[
                                                    competitor.driver_country.toLowerCase()
                                                ].name
                                            "
                                            :class="`border text-xl fi fi-${competitor.driver_country.toLowerCase()}`"
                                        ></span>
                                    </a>
                                    <template v-else>
                                        <span
                                            v-if="competitor.driver_country"
                                            :title="
                                                $page.props.app.countries[
                                                    competitor.driver_country.toLowerCase()
                                                ].name
                                            "
                                            :class="`border text-xl fi fi-${competitor.driver_country.toLowerCase()}`"
                                        ></span>
                                    </template>

                                    <span
                                        v-if="competitor.co_driver_country"
                                        :title="
                                            $page.props.app.countries[
                                                competitor.co_driver_country.toLowerCase()
                                            ].name
                                        "
                                        :class="`border text-xl fi fi-${competitor.co_driver_country.toLowerCase()}`"
                                    ></span>
                                </td>
                                <td
                                    :id="`competitor_id_${competitor.id}`"
                                    class="scroll-mt-[8.5rem] whitespace-nowrap px-3 py-4 text-sm md:scroll-mt-16"
                                >
                                    <FwbTooltip v-if="$page.props.can.view_sensitive_event">
                                        <template #trigger>
                                            <span
                                                class="cursor-pointer text-blue-600"
                                                @click="editCompetitor(competitor)"
                                            >
                                                {{ competitor.driver_full_name }}
                                                <template v-if="competitor.co_driver_full_name">
                                                    <br />{{ competitor.co_driver_full_name }}
                                                </template>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    class="inline size-5 text-blue-600 group-hover:text-blue-900"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                    />
                                                </svg>
                                            </span>
                                            <span
                                                v-if="competitor.user && competitor.user.email"
                                                class="cursor-pointer text-gray-400"
                                                @click="editCompetitor(competitor)"
                                            >
                                                <br />
                                                {{ competitor.user.name }} <br />
                                                {{ competitor.user.email }}
                                            </span>
                                        </template>
                                        <template #content>
                                            <div class="max-w-2xl">
                                                Driver phone: {{ competitor.reg_form.driver?.phone }} <br />
                                                <template v-if="competitor.reg_form.co_driver?.phone">
                                                    Co-Driver phone: {{ competitor.reg_form.co_driver?.phone }}
                                                    <br />
                                                </template>
                                                Vehicle reg. no:
                                                {{ competitor.reg_form.vehicle?.licence_plate }} <br />
                                            </div>
                                        </template>
                                    </FwbTooltip>
                                    <template v-else>
                                        {{ competitor.driver_full_name }}
                                        <template v-if="competitor.co_driver_full_name">
                                            <br />{{ competitor.co_driver_full_name }}
                                        </template>
                                    </template>
                                    <dl class="md:hidden">
                                        <dd class="mt-1 truncate">
                                            <template v-if="competitor.team_name">
                                                {{ competitor.team_name }} <br />
                                            </template>
                                            {{ competitor.vehicle_name }} <br />
                                            <span class="text-gray-500">({{ eventClass.name }})</span><br />
                                        </dd>
                                        <dd>
                                            <span
                                                class="mt-1 truncate"
                                                :class="{
                                                    'text-orange-300': competitor.status === 'pending',
                                                    'text-green-500': competitor.status === 'accepted',
                                                    'text-gray-300':
                                                        competitor.status === 'rejected' ||
                                                        competitor.status === 'tester',
                                                }"
                                                >{{ competitor.status }}</span
                                            >&nbsp;
                                            <TooltipIcon v-if="$page.props.user?.id === competitor.user_id">
                                                <div>
                                                    <div>
                                                        <span
                                                            v-if="!competitor.is_entry_fee_paid"
                                                            class="text-orange-300"
                                                        >
                                                            Entry fee: awaiting payment...
                                                        </span>
                                                        <span
                                                            v-if="competitor.is_entry_fee_paid"
                                                            class="text-green-500"
                                                        >
                                                            Entry fee: paid
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-if="
                                                            competitor?.reg_form?.general?.comment_to_organizer
                                                        "
                                                    >
                                                        Comment:
                                                        {{
                                                            competitor?.reg_form?.general?.comment_to_organizer
                                                        }}
                                                    </div>
                                                </div>
                                            </TooltipIcon>
                                        </dd>
                                    </dl>
                                </td>
                                <td class="hidden whitespace-nowrap px-3 py-4 text-sm md:table-cell">
                                    <template v-if="competitor.team_name">
                                        {{ competitor.team_name }} <br />
                                    </template>
                                    {{ competitor.vehicle_name }} <br />
                                    <span class="text-gray-500">({{ eventClass.name }})</span><br />
                                </td>
                                <td class="hidden whitespace-nowrap px-3 py-4 text-sm md:table-cell">
                                    <span
                                        class="capitalize"
                                        :class="{
                                            'text-orange-300': competitor.status === 'pending',
                                            'text-green-500': competitor.status === 'accepted',
                                            'text-gray-300':
                                                competitor.status === 'rejected' ||
                                                competitor.status === 'tester',
                                        }"
                                        >{{ competitor.status }}</span
                                    >&nbsp;
                                    <TooltipIcon v-if="$page.props.user?.id === competitor.user_id">
                                        <div>
                                            <div>
                                                <span
                                                    v-if="!competitor.is_entry_fee_paid"
                                                    class="text-orange-300"
                                                >
                                                    Entry fee: awaiting payment...
                                                </span>
                                                <span
                                                    v-if="competitor.is_entry_fee_paid"
                                                    class="text-green-500"
                                                >
                                                    Entry fee: paid
                                                </span>
                                            </div>
                                            <div v-if="competitor?.reg_form?.general?.comment_to_organizer">
                                                Comment:
                                                {{ competitor?.reg_form?.general?.comment_to_organizer }}
                                            </div>
                                        </div>
                                    </TooltipIcon>
                                </td>
                                <td
                                    v-if="$page.props.can.view_sensitive_event"
                                    class="px-3 py-4 text-sm"
                                    :class="{
                                        'text-green-500': competitor.is_entry_fee_paid,
                                        'text-red-600': !competitor.is_entry_fee_paid,
                                    }"
                                >
                                    {{ competitor.is_entry_fee_paid ? "Yes" : "No" }}
                                </td>
                                <td
                                    v-if="$page.props.can.view_sensitive_event"
                                    class="px-3 py-4 text-sm"
                                >
                                    {{
                                        DateTime.fromISO(competitor.created_at)
                                            .setZone($page.props.event.timezone)
                                            .toLocaleString(DateTime.DATETIME_SHORT)
                                    }}
                                </td>
                                <td
                                    v-if="$page.props.can.view_sensitive_event"
                                    class="px-3 py-4 text-sm"
                                >
                                    {{ competitor.reg_form.general.comment_to_organizer }}
                                </td>

                                <td
                                    v-if="$page.props.can.view_sensitive_event"
                                    class="px-3 py-4 text-sm"
                                >
                                    <button
                                        class="group flex cursor-pointer items-center space-x-1 p-2 font-semibold md:p-1"
                                        @click="editCompetitor(competitor)"
                                    >
                                        <span class="text-blue-600 group-hover:text-blue-900"> Edit </span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="inline size-5 text-blue-600 group-hover:text-blue-900"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            stroke-width="2"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                            />
                                        </svg>
                                    </button>
                                    <a
                                        :href="
                                            route('event.competitor-entry-form-pdf', {
                                                event_hashid: $page.props.event.hashid,
                                                competitor_id: competitor.id,
                                            })
                                        "
                                    >
                                        <PrinterIcon class="inline size-4"></PrinterIcon>
                                        Entry Form (PDF)
                                    </a>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <dialog-modal
            :show="modalSendEmail.show"
            @close="closeModals()"
        >
            <template #title>Send an email to competitors</template>

            <template #content>
                <div class="mt-4">
                    <p class="text-gray-500">From: RoadbookRally.com &lt;events@roadbookrally.com&gt;</p>
                    <p>Reply-to: {{ $page.props.user.name }} &lt;{{ $page.props.user.email }}&gt;</p>
                    <div class="flex-1">
                        <div class="mt-4">
                            <jet-label
                                for="modalSendEmail-subject"
                                value="Email Subject"
                            />
                            <input
                                id="modalSendEmail-subject"
                                ref="modalSendEmail-subject"
                                v-model="modalSendEmailForm.subject"
                                type="text"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                disabled
                                :class="{
                                    'border-red-600 ': modalSendEmailForm.errors.subject,
                                }"
                            />
                            <input-error
                                :message="modalSendEmailForm.errors.subject"
                                class="mt-2"
                            />
                        </div>
                    </div>

                    <div class="mt-4">
                        <jet-label
                            for="modalSendEmail-contents"
                            value="Email contents"
                        />
                        <jet-textarea
                            v-model="modalSendEmailForm.contents"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            :class="{
                                'border-red-600 ': modalSendEmailForm.errors.contents,
                            }"
                        >
                        </jet-textarea>
                        <input-error
                            :message="modalSendEmailForm.errors.contents"
                            class="mt-2"
                        />
                    </div>

                    <div class="mt-4">
                        <jet-label value="Choose recipients" />
                        <div class="mt-1 flex gap-6 text-sm">
                            <label class="flex items-center">
                                <input
                                    v-model="modalSendEmailForm.recipient_mode"
                                    type="radio"
                                    value="filters"
                                    class="border-gray-300 text-blue-600 focus:ring-blue-200"
                                />
                                <span class="ml-2">By status / class / country</span>
                            </label>
                            <label class="flex items-center">
                                <input
                                    v-model="modalSendEmailForm.recipient_mode"
                                    type="radio"
                                    value="individual"
                                    class="border-gray-300 text-blue-600 focus:ring-blue-200"
                                />
                                <span class="ml-2">Individual riders</span>
                            </label>
                        </div>
                        <input-error
                            :message="modalSendEmailForm.errors.recipient_competitor_ids"
                            class="mt-2"
                        />
                    </div>

                    <div
                        v-if="modalSendEmailForm.recipient_mode === 'filters'"
                        class="mt-4"
                    >
                        <p class="text-sm text-gray-500">
                            Tick nothing to send to everybody. Each tick makes the group smaller. The counts
                            show how many riders you reach now.
                        </p>
                        <div class="mt-2 grid gap-4 sm:grid-cols-3">
                            <div class="rounded-md border border-gray-300 p-2">
                                <p class="text-sm font-semibold">Status</p>
                                <label
                                    v-for="status in competitorStatuses"
                                    :key="'mailstatus' + status"
                                    class="flex items-center text-sm"
                                >
                                    <jet-checkbox
                                        v-model:checked="modalSendEmailForm.recipient_statuses"
                                        :value="status"
                                    />
                                    <span
                                        class="ml-2 capitalize"
                                        :class="{
                                            'text-orange-300': status === 'pending',
                                            'text-green-500': status === 'accepted',
                                            'text-gray-300': status === 'rejected' || status === 'tester',
                                        }"
                                        >{{ status }}</span
                                    >
                                    <span class="ml-1 text-gray-500">({{ statusCount(status) }})</span>
                                </label>
                            </div>

                            <div class="max-h-48 overflow-auto rounded-md border border-gray-300 p-2">
                                <p class="text-sm font-semibold">Class</p>
                                <label
                                    v-for="eventClass in event.classes"
                                    :key="'mailclass' + eventClass.id"
                                    class="flex items-center text-sm"
                                >
                                    <jet-checkbox
                                        v-model:checked="modalSendEmailForm.recipient_class_ids"
                                        :value="eventClass.id"
                                    />
                                    <span class="ml-2"
                                        >{{ eventClass.name }}
                                        <span class="text-gray-500">({{ classCount(eventClass.id) }})</span>
                                    </span>
                                </label>
                            </div>

                            <div class="max-h-48 overflow-auto rounded-md border border-gray-300 p-2">
                                <p class="text-sm font-semibold">Country</p>
                                <label
                                    v-for="country in competitorCountries"
                                    :key="'mailcountry' + country.code"
                                    class="flex items-center text-sm"
                                >
                                    <jet-checkbox
                                        v-model:checked="modalSendEmailForm.recipient_countries"
                                        :value="country.code"
                                    />
                                    <span class="ml-2"
                                        ><span :class="`border fi fi-${country.code.toLowerCase()}`"></span>
                                        {{ country.name }}
                                        <span class="text-gray-500">({{ countryCount(country.code) }})</span>
                                    </span>
                                </label>
                                <p
                                    v-if="!competitorCountries.length"
                                    class="text-sm text-gray-500"
                                >
                                    No countries entered yet.
                                </p>
                            </div>
                        </div>
                        <p class="mt-4 text-sm">
                            {{ selectedRecipients.length }} of {{ allCompetitors.length }} competitor(s) will
                            get this email.
                            <button
                                type="button"
                                class="ml-2 text-blue-600 hover:text-blue-900"
                                @click="clearRecipientFilters()"
                            >
                                Reset to all
                            </button>
                        </p>
                    </div>

                    <div
                        v-if="modalSendEmailForm.recipient_mode === 'individual'"
                        class="mt-4"
                    >
                        <input
                            v-model="riderFilter"
                            type="search"
                            placeholder="Filter riders by name..."
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <div class="mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-300 p-2">
                            <div
                                v-for="eventClass in filteredClasses"
                                :key="'mailrider' + eventClass.id"
                                class="mb-2"
                            >
                                <p class="text-sm font-semibold">{{ eventClass.name }}</p>
                                <label
                                    v-for="competitor in eventClass.competitors"
                                    :key="'mailcomp' + competitor.id"
                                    class="ml-2 flex items-center text-sm"
                                >
                                    <jet-checkbox
                                        v-model:checked="modalSendEmailForm.recipient_competitor_ids"
                                        :value="competitor.id"
                                    />
                                    <span class="ml-2">
                                        {{ riderNames(competitor) }}
                                        <span
                                            :class="{
                                                'text-orange-300': competitor.status === 'pending',
                                                'text-green-500': competitor.status === 'accepted',
                                                'text-gray-300':
                                                    competitor.status === 'rejected' ||
                                                    competitor.status === 'tester',
                                            }"
                                            >({{ competitor.status }})</span
                                        >
                                    </span>
                                </label>
                            </div>
                            <p
                                v-if="!filteredClasses.length"
                                class="text-sm text-gray-500"
                            >
                                No riders match "{{ riderFilter }}".
                            </p>
                        </div>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ modalSendEmailForm.recipient_competitor_ids.length }} rider(s) selected.
                        </p>
                    </div>
                </div>
            </template>

            <template #footer>
                <Button
                    color="white"
                    @click="closeModals()"
                >
                    Cancel
                </Button>
                <jet-button
                    class="ml-3"
                    :class="{ 'opacity-25': modalSendEmailForm.processing }"
                    :disabled="modalSendEmailForm.processing"
                    @click="modalSendEmailFormSendEmail()"
                >
                    <span> Send Email</span>
                </jet-button>
            </template>
        </dialog-modal>

        <dialog-modal
            :show="modalRegistrationInvites.show"
            @close="closeModals()"
        >
            <template #title>Registration invite links</template>

            <template #content>
                <p>
                    Each link allows one person to register even though registration is closed. A link becomes
                    invalid after one successful registration, when it is revoked, or when the event ends.
                </p>

                <p
                    v-if="event.visibility === 'private'"
                    class="mt-4 text-sm text-red-600"
                >
                    This event is private, so invite links only work for people who can already view the event.
                </p>

                <p
                    v-if="registration_invites.length === 0"
                    class="mt-4 text-sm text-gray-500"
                >
                    No invite links yet.
                </p>

                <ul
                    v-else
                    class="mt-4 flex flex-col gap-3"
                >
                    <li
                        v-for="invite in registration_invites"
                        :key="invite.id"
                        class="flex flex-col gap-1 rounded-md border border-gray-200 p-3"
                    >
                        <div class="flex items-center justify-between gap-3 text-sm text-gray-500">
                            <span>
                                Created {{ invite.created_at }}
                                <template v-if="invite.used_at">· used {{ invite.used_at }}</template>
                                <template v-if="invite.used_by">by {{ invite.used_by }}</template>
                            </span>
                            <span
                                v-if="invite.used_at"
                                class="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                            >
                                Used
                            </span>
                            <span
                                v-else
                                class="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700"
                            >
                                Unused
                            </span>
                        </div>
                        <div
                            v-if="!invite.used_at"
                            class="flex items-center gap-2"
                        >
                            <input
                                type="text"
                                readonly
                                :value="invite.url"
                                class="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                @focus="($event.target as HTMLInputElement).select()"
                            />
                            <Button
                                v-if="!event.is_ended"
                                color="white"
                                @click="copyToClipboard(invite.url)"
                            >
                                Copy
                            </Button>
                            <Button
                                color="white"
                                @click="deleteRegistrationInvite(invite)"
                            >
                                Revoke
                            </Button>
                        </div>
                    </li>
                </ul>
            </template>

            <template #footer>
                <Button
                    color="white"
                    @click="closeModals()"
                >
                    Close
                </Button>
                <jet-button
                    v-if="!event.is_ended"
                    class="ml-3"
                    :class="{ 'opacity-25': modalRegistrationInvites.isCreating }"
                    :disabled="modalRegistrationInvites.isCreating"
                    @click="createRegistrationInvite()"
                >
                    Create invite link
                </jet-button>
            </template>
        </dialog-modal>

        <dialog-modal
            :show="modalCompetitorNumbers.show"
            @close="closeModals()"
        >
            <template #title>Generate competitor numbers</template>
            <template #content>
                <div class="mt-0">
                    <p
                        v-if="event.is_started"
                        class="text-red-500"
                    >
                        Event is started! Are you sure you want to regenerate competitor numbers?<br />
                    </p>
                    <br />
                    <hr />
                    <br />
                    <p>
                        For all <span class="text-green-500">accepted</span> competitors
                        <strong v-if="!modalCompetitorNumbers.overwriteAll">without a number</strong>
                        <strong v-else>regardless of existing numbers</strong>
                        assigned
                    </p>
                    <br />
                    <hr />
                    <br />
                    <div class="mb-4">
                        <JetLabel>
                            <div class="flex items-center">
                                <JetCheckbox
                                    v-model:checked="modalCompetitorNumbers.overwriteAll"
                                    name="overwrite_all"
                                />
                                <div class="ml-2">
                                    Overwrite numbers for all competitors
                                    <div class="text-sm text-gray-500">
                                        If checked, will regenerate numbers for ALL competitors, not just those
                                        without numbers
                                    </div>
                                </div>
                            </div>
                        </JetLabel>
                    </div>
                    <br />
                    <hr />
                    <br />
                    <div class="space-y-4">
                        <p>
                            Group numbers by class: <br />
                            <span class="text-sm text-gray-400"
                                >(numbers are assigned separately within each class as defined in the
                                <Link
                                    :href="
                                        route('event.edit.itinerary', {
                                            hashid: $page.props.event.hashid,
                                            seo_slug: $page.props.event.seo_slug,
                                        })
                                    "
                                    >Event Itinerary</Link
                                >)
                            </span>
                        </p>
                        <jet-button @click="regenerateCompetitorNumbers('order_registration', true)">
                            Assign in registration order
                        </jet-button>
                        <br />
                        <jet-button @click="regenerateCompetitorNumbers('order_random', true)">
                            Assign in random order
                        </jet-button>
                        <br />
                    </div>

                    <br />
                    <hr />
                    <br />

                    <div class="space-y-4">
                        Assign numbers from one combined list: <br />
                        <span class="text-sm text-gray-400">(ignores classes)</span> <br />
                        <jet-button @click="regenerateCompetitorNumbers('order_registration')">
                            Assign in registration order
                        </jet-button>
                        <br />
                        <jet-button @click="regenerateCompetitorNumbers('order_random')">
                            Assign in random order
                        </jet-button>
                        <br />
                    </div>
                    <br />
                    <hr />
                    <p class="mt-4 text-sm">*You can regenerate competitor numbers at any time.</p>
                </div>
            </template>

            <template #footer>
                <Button
                    color="white"
                    @click="closeModals()"
                >
                    Cancel
                </Button>
            </template>
        </dialog-modal>
    </EventPublicWrapper>
</template>
